import { showToggle } from './toggles'
import { setMode } from './app'
import getOrientation from '../../utils/getOrientation'
import ReactGA from 'react-ga'
import {
  DONE_WITH_LOCAL_IMAGE,
  STORE_LOCAL_IMAGE,
  UPLOAD_COMPLETE
} from '../actionTypes'
import { firebaseAction } from '../utils'
import firebase from 'firebase/app'

const timestamp = firebase.database.ServerValue.TIMESTAMP

// itterate over multiple files and upload each one
export const uploadFilesToQueue = files => (dispatch, getState) =>
  Array.prototype.forEach.call(files, file => dispatch(uploadFileToQueue(file)))

export const uploadFileToQueue = file => async dispatch => {
  // only images
  if (!file.type.match(/^image\//)) return
  const info = await getInfo({ file })
  const uploadId = await dispatch(storeLocalImage(info.src))
  dispatch(writeToQueue(info, uploadId))
  dispatch(uploadFile(file, uploadId))
}

// figure out if it's a dataURI or url for external source
export const urlToQueue = (url, extra) =>
  url.match(/^data:/)
    ? dataUrlToQueue(url, extra)
    : externalUrlToQueue(url, extra)

export const dataUrlToQueue = (url, extra = {}) => async dispatch => {
  const info = await getInfo({ url })
  const uploadId = await dispatch(storeLocalImage(info.src))
  dispatch(writeToQueue({ ...info, ...extra }, uploadId))
  dispatch(uploadDataUri(url, uploadId))
}

export const externalUrlToQueue = (url, extra = {}) => async dispatch => {
  const info = await getInfo({ url })
  const uploadId = await dispatch(storeLocalImage(info.src))
  dispatch(writeToQueue({ ...info, ...extra }, uploadId, url))
  dispatch({ type: UPLOAD_COMPLETE, payload: { uploadId } })
}

export const uploadToBlock = (files, block) =>
  firebaseAction(async (update, { dispatch, getAuth }) => {
    const file = files[0]
    // only images
    if (!file.type.match(/^image\//)) return
    const info = await getInfo({ file })
    const uploadId = await dispatch(storeLocalImage(info.src))
    const auth = getAuth()
    const blockAspect = block.aspect || block.height / block.width
    const path = `blocks/${block.board}/${block.id}/`
    let forward = {
      [path + 'image']: {
        width: info.width,
        height: info.height,
        aspect: info.aspect,
        orientation: info.orientation || null,
        pendingUpload: uploadId
      },
      [path + 'name']: info.name || '',
      // [path + 'height']: block.width * info.aspect,
      [path + 'filesize']: info.filesize || 0,
      [path + 'aspect']: blockAspect,
      [`/pendingUploads/${uploadId}`]: {
        dateCreated: timestamp,
        user: { id: auth.uid, displayName: auth.displayName },
        locations: { [block.id]: path },
        size: info.filesize || null,
        name: info.name || ''
      }
    }
    if (info.aspect < blockAspect) {
      const blockHeight = block.width * blockAspect
      const contentWidth = blockHeight / info.aspect
      forward[`blocks/${block.board}/${block.id}/image`].crop = {
        left: (contentWidth - block.width) / 2 / block.width,
        top: 0,
        scale: blockAspect / info.aspect
      }
    } else {
      const contentHeight = block.width * info.aspect
      const blockHeight = block.width * blockAspect
      forward[`blocks/${block.board}/${block.id}/image`].crop = {
        left: 0,
        top: (contentHeight - blockHeight) / 2 / blockHeight,
        scale: 1
      }
    }
    update({ forward })
    dispatch(uploadFile(file, uploadId))
    ReactGA.event({ category: 'Blocks', action: 'Replace content' }, ['app'])
  })

export const storeLocalImage = src => async (dispatch, getState) => {
  const uploadId = firebase
    .database()
    .ref()
    .push().key

  dispatch({
    type: STORE_LOCAL_IMAGE,
    payload: {
      uploadId,
      src
    }
  })

  return uploadId
}

export const writeToQueue = (info, uploadId, externalUrl) =>
  firebaseAction(async (update, { getAuth, getKey, dispatch }) => {
    const blockId = getKey()
    uploadId = uploadId || getKey()
    const auth = getAuth()
    const queuePath = `/queues/${auth.uid}/${blockId}`

    const forward = {
      [queuePath]: {
        image: {
          width: info.width,
          height: info.height,
          aspect: info.aspect,
          orientation: info.orientation || null,
          pendingUpload: uploadId,
          sourceUrl: externalUrl || null,
          originalLink: info.originalLink || null
        },
        name: info.name || '',
        filesize: info.filesize || 0,
        aspect: info.aspect,
        dateCreated: timestamp,
        id: blockId,
        owner: auth.uid
      },
      [`/pendingUploads/${uploadId}`]: {
        dateCreated: timestamp,
        user: { id: auth.uid, displayName: auth.displayName },
        locations: { [blockId]: queuePath },
        size: info.filesize || null,
        name: info.name || '',
        sourceUrl: externalUrl || null,
        originalLink: info.originalLink || null
      }
    }

    update({ forward })

    dispatch(showToggle('queue'))
    dispatch(setMode('edit'))
  })

/*
 * Utils
 */

const watchUpload = (uploadTask, uploadId) => async dispatch => {
  const database = firebase.database()

  const progress = snapshot => {
    database.ref(`/pendingUploads/${uploadId}`).update({
      complete: snapshot.bytesTransferred,
      size: snapshot.totalBytes
    })
  }

  const error = error => {
    console.error('Upload Error', uploadId, error)
    // A full list of error codes is available at
    // https://firebase.google.com/docs/storage/web/handle-errors
  }

  const done = () => {
    dispatch({ type: UPLOAD_COMPLETE, payload: { uploadId } })
    uploadTask.snapshot.ref.getDownloadURL().then(function(downloadURL) {
      database.ref(`/pendingUploads/${uploadId}/url`).set(downloadURL)
    })
  }

  uploadTask.on(firebase.storage.TaskEvent.STATE_CHANGED, progress, error, done)
}

const uploadFile = (file, uploadId) => async dispatch => {
  const upload = firebase
    .storage()
    .ref('images/' + uploadId)
    .put(file, metadata(file.name))

  dispatch(watchUpload(upload, uploadId))
}

const uploadDataUri = (src, uploadId) => async (dispatch, getState) => {
  const upload = firebase
    .storage()
    .ref('images/' + uploadId)
    .putString(src, firebase.storage.StringFormat.DATA_URL, metadata(uploadId))

  dispatch(watchUpload(upload, uploadId))
}

const loadImage = src =>
  new Promise((resolve, reject) => {
    var image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
  })

const metadata = filename => ({
  contentDisposition: 'attachment; filename=' + filename
})

const dataUriFilesize = src => {
  const base64Length = src.length - (src.indexOf(',') + 1)
  const padding =
    src.charAt(src.length - 2) === '='
      ? 2
      : src.charAt(src.length - 1) === '='
      ? 1
      : 0
  return base64Length * 0.75 - padding
}

const createObjectURL = file =>
  (window.URL || window.webkitURL).createObjectURL(file)
const revokeObjectURL = file =>
  (window.URL || window.webkitURL).revokeObjectURL(file)

const getInfo = async ({ file, url }) => {
  const info = {}

  if (file) {
    info.name = file.name
    info.filesize = file.size
    info.src = createObjectURL(file)
    info.orientation = await getOrientation(file)
  } else if (url.match(/^data:/)) {
    info.filesize = dataUriFilesize(url)
    info.src = url
  } else {
    const nameMatch = url.match(/^.+\/([^?/\n]+)/)
    if (nameMatch) info.name = nameMatch[1]
    info.src = url
  }

  const image = await loadImage(info.src)
  info.width = image.width
  info.height = image.height
  info.aspect = image.height / image.width
  if (info.orientation && info.orientation >= 5) info.aspect = 1 / info.aspect

  return info
}

export const doneWithLocalImage = src => {
  return {
    type: DONE_WITH_LOCAL_IMAGE,
    // pendingUpload is gone by the time this is called
    payload: { src }
  }
}

export const uploadReducer = (state = {}, { type, payload }) => {
  switch (type) {
    case STORE_LOCAL_IMAGE:
      return { ...state, [payload.uploadId]: { localUrl: payload.src } }
    case UPLOAD_COMPLETE:
      return {
        ...state,
        [payload.uploadId]: { ...state[payload.uploadId], uploaded: true }
      }
    case DONE_WITH_LOCAL_IMAGE:
      const uploadId = Object.keys(state).find(
        id => state[id].localUrl === payload.src
      )
      // clear the local image data
      if (uploadId && state[uploadId]) revokeObjectURL(payload.src)
      const { [uploadId]: _, ...others } = state
      return others
    default:
      return state
  }
}
